import React, { useState, useEffect, useRef } from "react";
import { format } from 'date-fns';
import './PopeyeShipmentList.scss';
import { Table, Tooltip } from 'antd';
import { getIcon, getStateSmallIcon, getFlag } from '../../utils/images';
import { getStateColorMap } from '../../utils/utils';
import { ReactSVG } from "react-svg";
import scrollIntoView from 'scroll-into-view';

const PopeyeShipmentList = ({ shipments, setSelectedShipment }) => {
  const [currentRow, setCurrentRow] = useState(0);
  const scrollableContentRef = useRef(null);
  const hasCompany = shipments.some((shipment) => shipment.company);
  const headerTitleClassName = hasCompany
    ? "header-titles-with-company"
    : "header-titles-no-company";
  const shipmentAndCargoWidth = hasCompany ? "15%" : "20%"
  const fromAndToWidth = hasCompany ? "10%" : "15%"

  useEffect(() => {
    const totalRows = shipments.length;
    setSelectedShipment(shipments[currentRow])

    const interval = setInterval(() => {
      totalRows && setCurrentRow((currentRow + 1) % totalRows)
    }, process.env.REACT_APP_POPEYE_CYCLE_DURATION * 1000); // Change every 5 seconds

    scrollableContentRef.current && scrollIntoView(document.querySelector(".highlighted-row"))

    return () => clearInterval(interval);
  }, [currentRow])

  const getStatusClass = (state) =>
    state === 'confirmed' || state === 'containers_assigned' || state === 'pick' || state === 'inland'
      ? 'status-scheduled'
      : state === 'pol' || state === 'sea'
      ? 'status-sailing'
      : state === 'pod' || state === 'customs' || state === "gateout" || state === "emptyreturn" || state === 'delivered'
      ? 'status-discharged'
      : 'status-default'; // Default class for undefined states

  const columns = [
    {
      title: '#',
      key: 'incremental_id',
      render: (_, __, index) => (
        <div>{index + 1}</div>
      ),
      width: "5%",
    },
    {
      title: 'Ship. Number',
      dataIndex: 'booking_number',
      key: 'booking_number',
      render: (booking_number, shipment) => (
        <div className="ship-number" id={shipment.id}>
          <div className="logos">
            {shipment.delay && (
              <Tooltip>
                <div className="attention-logo" >
                  <img className="attention-logo-img" src={getStateSmallIcon('attention')} alt="attention Logo" />
                </div>
              </Tooltip>
            )}
            <Tooltip title={shipment.shipping_line?.name}>
              <div className="logo">
                <img src={shipment.shipping_line?.icon || getIcon('logo-mini')} alt="logo" />
              </div>
            </Tooltip>
          </div>
          <div>
            { 
              shipment.group?.length > 1
              ? (
                <div className="number">
                  {shipment.group.length} Shipments
                </div>
              ) : booking_number && (
                <div className="number">
                  <Tooltip title={booking_number}>
                    {booking_number}
                  </Tooltip>
                </div> 
              )
            }
            {
              shipment.bl && (
                <div className="sub-number">
                  <Tooltip title={shipment.bl}>
                    {shipment.bl}
                  </Tooltip>
                </div>
              )
            }
            {  
              shipment.client_ref && (
                <div className="sub-number">
                  <Tooltip title={shipment.client_ref}>
                    {shipment.client_ref}
                  </Tooltip>
                </div>
              )
            }
          </div>

        </div>
      ),
      width: shipmentAndCargoWidth,
    },
    { ...(hasCompany && {
        title: 'Company',
        dataIndex: 'company',
        key: 'company',
        render: (company) => (company ? <div>{company}</div> : null),
        width: "10%",
      })
    },
    {
      title: 'Depart At',
      dataIndex: 'pol',
      key: 'pol',
      render: (pol) => (
        <div>
          {pol?.date ? format(new Date(pol.date), 'dd MMM').toUpperCase() : ''}
        </div>
      ),
      width: "10%",
    },
    {
      title: 'From',
      dataIndex: 'pol',
      key: 'pol',
      render: (pol) => (
        <div className="Popeye-Point">
          <div>
            <img src={pol?.code ? getFlag(pol?.code) : getIcon('fetch', 'gif')} />
          </div>
          <div>{pol?.unlocode}</div>
        </div>
      ),
      width: fromAndToWidth,
    },
    {
      title: '',
      dataIndex: '',
      key: '',
      render: () => (
        <div><img src={getIcon('white-arrow-icon')} /></div>
      ),
      width: "5%",
    },
    {
      title: 'To',
      dataIndex: 'pod',
      key: 'pod',
      render: (pod) => (
        <div className="Popeye-Point">
          <div>
            <img src={pod?.code ? getFlag(pod?.code) : getIcon('fetch', 'gif')} />
          </div>
          <div>{pod?.unlocode}</div>
        </div>
      ),
      width: fromAndToWidth,
    },
    {
      title: 'Arrive At',
      dataIndex: 'pod',
      key: 'pod',
      render: (pod) => (
        <div>
          {pod?.date ? format(new Date(pod.date), 'dd MMM').toUpperCase() : ''}
        </div>
      ),
      width: "10%",
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
      render: (cargo) => (
        <div>{cargo}</div> || 
        <div><img src={getIcon('cargo-fetching')} /></div>
      ),
      width: shipmentAndCargoWidth,
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      render: (state) => (
        <div className={`status ${getStatusClass(state)}`}>
          {state ? (
            <>
              <div className="status-icon">
                <ReactSVG src={getStateSmallIcon(state)} />
              </div>
              <div className="status-text">
                <p className="tag" color={getStateColorMap(state).color }>
                  {state === "sea" ? "Sailed" : getStateColorMap(state).title}
                </p>
              </div>
            </>
          ) : (
            <div className="status-fetching">
              <img src={getIcon('status-fetching')} alt="fetching icon" />
            </div>
          )}
        </div>
      ),
      width: "15%",
    },
  ];

  return (
    <div className="PopeyeShipmentList">
      <div className="list-wrapper">
        <div className="elements-header">
          <div className={headerTitleClassName} >
            <div className="title">#</div>
            <div className="title">Ship. Number</div>
            {hasCompany && <div className="title">Company</div>}
            <div className="title">Depart At</div>
            <div className="title">From</div>
            <div className="title"></div>
            <div className="title">To</div>
            <div className="title">Arrive At</div>
            <div className="title">Cargo</div>
            <div className="title">Status</div>
          </div>
        </div>
        <div className="scrollable-content" ref={scrollableContentRef}>
          {shipments.length
            ?
            <div className="elements">
              <div className="elements-table">
                <Table
                  rowClassName={(record, index) => index === currentRow ? 'highlighted-row' : 'ant-table-row'}
                  dataSource={shipments}
                  columns={columns}
                  pagination={false}
                  rowKey="id"
                  onRow={(record) => ({
                    onClick: () => {
                      const shipmentDetailsLink = `/shipment_management/explore#${record.id}`;
                      window.open(shipmentDetailsLink, "_blank");
                    },
                  })}
                />
              </div>
            </div>
            :
            <div className="no-elements">
              <span>It seems you don't have any shipments, start by adding your shipments </span>
            </div>
          }
        </div>
      </div>
    </div>
  );
}

export default PopeyeShipmentList;
